import Image from "next/image";
import TechBadge, { TechBadgeType } from "../Badge/TechBadge";
import { motion } from "framer-motion";

interface TechBadgeListProps {
  badges: TechBadgeType[];
}

interface DescriptionListProps {
  items: { title: string; content: string }[];
}

interface TimelineItem {
  date: string;
  title: string;
  description: React.ReactNode;
  techs?: TechBadgeType[];
  listItems?: { title: string; content: string }[];
}

export const Timeline = () => {
  const createDescription = (
    intro: string,
    items: { title: string; content: string }[],
    techs: TechBadgeType[]
  ): React.ReactNode => (
    <motion.div
      initial={{ opacity: 0, y: 100 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, delay: 0.4, ease: "easeInOut" }}
      viewport={{ once: true }}
      className="text-slate-500 text-justify"
    >
      <p>{intro}</p>
      <DescriptionList items={items} />
      <TechBadgeList badges={techs} />
    </motion.div>
  );

  const kozoTimelines: TimelineItem[] = [
    {
      date: "Dec, 2024",
      title: "Hệ thống quản lý công việc online",
      description: createDescription(
        "Hệ thống việc làm gồm 3 thành phần:",
        [
          {
            title: "Client",
            content:
              "Dành cho người dùng đăng ký tài khoản, ứng tuyển công việc, điểm danh và theo dõi lịch làm việc.",
          },
          {
            title: "Admin",
            content:
              "Quản lý toàn bộ hệ thống, bao gồm tạo và duyệt công việc, chấm công, tổ chức phỏng vấn, quản lý thanh toán, và theo dõi hiệu quả làm việc.",
          },
          {
            title: "Đối tác",
            content:
              "Cho phép các doanh nghiệp hoặc cá nhân tạo công việc, theo dõi ứng viên, và phối hợp với Admin để hoàn thiện quy trình tuyển dụng.",
          },
        ],
        ["html", "react", "laravel", "typescript", "tailwind", "aws"]
      ),
    },
    {
      date: "Nov, 2023",
      title: "Hệ thống đầu tư cổ phiếu nhà đất",
      description: createDescription(
        "Ứng dụng được chia thành hai hệ thống:",
        [
          {
            title: "Hệ thống quản lý",
            content:
              "Quản lý người dùng, khách hàng, cổ phiếu, thống kê, đầu tư, chi tiêu, thừa kế và các chức năng liên quan.",
          },
          {
            title: "Hệ thống đầu tư",
            content:
              "Hỗ trợ người dùng mua cổ phiếu, xây dựng quỹ đầu tư cá nhân, quản lý giao dịch mua/bán cổ phiếu, và các hoạt động đầu tư khác.",
          },
        ],
        ["html", "react", "next", "typescript", "tailwind", "laravel", "aws"]
      ),
    },
    {
      date: "Jun, 2022",
      title: "Hệ thống khám bệnh và ship thuốc online",
      description: createDescription(
        "Dự án cung cấp giải pháp chăm sóc sức khỏe từ xa, gồm hai hệ thống:",
        [
          {
            title: "Client",
            content:
              "Dành cho người dùng, hỗ trợ đặt lịch khám, khám bệnh qua video call, thanh toán, đặt thuốc, và theo dõi đơn hàng. Sử dụng theo dạng webview bằng Flutter.",
          },
          {
            title: "Clinic",
            content:
              "Dành cho phòng khám, giúp quản lý lịch làm việc, gọi video với bệnh nhân, kê thuốc, ship thuốc và theo dõi hồ sơ y tế.",
          },
        ],
        ["html", "react", "typescript", "tailwind", "nodejs", "aws"]
      ),
    },
  ];

  const freelancerTimeLine: TimelineItem[] = [
    {
      date: "Jun, 2024",
      title: "Blog + Headless CMS (Next.js + Laravel + Filament)",
      description:
        "Hệ thống quản lý blog quy mô lớn. Với frontend là NextJS và backend là Laravel, hệ thống tích hợp Filament để làm Headless CMS.",
      techs: ["html", "react", "next", "laravel", "tailwind"],
    },
    {
      date: "Sep, 2024",
      title: "Website Novas",
      description:
        "Trang web portfolio cho hệ thống tự động hóa Novas, một trong những đơn vị lớn nhất tại Đà Nẵng. Website giới thiệu các sản phẩm và giải pháp tự động hóa hiện đại, tích hợp hệ thống liên hệ và yêu cầu báo giá trực tuyến, đáp ứng nhu cầu doanh nghiệp và khách hàng cá nhân.",
      techs: ["html", "react", "next", "laravel", "tailwind"],
    },
    {
      date: "Jan, 2023",
      title: "Các hệ thống WordPress",
      description: "",
      techs: ["html", "wordpress", "tailwind", "mysql"],
      listItems: [
        {
          title: "Onefilm",
          content: "Website giới thiệu công ty quay phim tại Đà Nẵng.",
        },
        { title: "Oani Spa", content: "Website hệ thống spa lớn Đà Nẵng." },
        {
          title: "GSL Holdings Group",
          content: "Website giới thiệu, mua bán nội thất trực tuyến.",
        },
        {
          title: "HTML5 Game",
          content:
            "Web game giải trí, cung cấp các trò chơi phong phú và giao diện thân thiện.",
        },
      ],
    },
    {
      date: "Dec, 2022",
      title: "Tra cứu thần số học",
      description:
        "Ứng dụng cung cấp thông tin chuyên sâu về thần số học, giúp người dùng tra cứu các chỉ số cá nhân, nhận các dự đoán và phân tích liên quan đến vận mệnh và tính cách dựa trên ngày sinh.",
      techs: ["html", "react", "express", "typescript", "tailwind"],
    },
  ];

  return (
    <div className="mt-12">
      <motion.h3
        initial={{ opacity: 0, y: 100 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.2, ease: "easeInOut" }}
        viewport={{ once: true }}
        className="font-semibold text-primary dark:text-[#45c3dc]"
      >
        Kinh nghiệm làm việc:
      </motion.h3>
      <div className="w-full flex flex-wrap mt-4 ">
        <div className="w-full md:w-1/2 pr-2">
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2, ease: "easeInOut" }}
            viewport={{ once: true }}
            className="flex items-center gap-4"
          >
            <Image
              alt="Kozocom Logo"
              src="/images/logo/Kozo.webp"
              width={60}
              height={60}
            />
            <h5 className="font-bold text-xl">
              KOZOCOM | Full-stack Developer
            </h5>
          </motion.div>
          {kozoTimelines.map((timeline) => (
            <motion.div
              initial={{ opacity: 0, y: 100 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: "easeInOut" }}
              viewport={{ once: true }}
              className="relative pl-8 sm:pl-32 py-4 group"
              key={timeline.title}
            >
              <div className="flex flex-col sm:flex-row items-start mb-1 group-last:before:hidden before:absolute before:left-2 sm:before:left-0 before:h-full before:px-px before:bg-slate-300 sm:before:ml-[6.5rem] before:self-start before:-translate-x-1/2 before:translate-y-3 after:absolute after:left-2 sm:after:left-0 after:w-2 after:h-2 after:bg-indigo-600 after:border-4 after:box-content after:border-slate-50 after:rounded-full sm:after:ml-[6.5rem] after:-translate-x-1/2 after:translate-y-1.5">
                <motion.time
                  initial={{ opacity: 0, y: 100 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.2, ease: "easeInOut" }}
                  viewport={{ once: true }}
                  className="sm:absolute left-0 translate-y-0.5 inline-flex items-center justify-center text-xs font-semibold uppercase w-20 h-6 mb-3 sm:mb-0 text-emerald-600 bg-emerald-100 rounded-full"
                >
                  {timeline.date}
                </motion.time>
                <motion.div
                  initial={{ opacity: 0, y: 100 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.3, ease: "easeInOut" }}
                  viewport={{ once: true }}
                  className="text-lg font-bold text-slate-900 dark:text-white"
                >
                  {timeline.title}
                </motion.div>
              </div>
              {timeline.description}
            </motion.div>
          ))}
          <div className="relative pl-8 sm:pl-32 py-4 group">
            <div className="flex flex-col sm:flex-row items-start mb-1 group-last:before:hidden before:absolute before:left-2 sm:before:left-0 before:h-full before:px-px before:bg-slate-300 sm:before:ml-[6.5rem] before:self-start before:-translate-x-1/2 before:translate-y-3"></div>
          </div>
        </div>
        <div className="w-full md:w-1/2 pl-2 mt-6 md:mt-0">
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2, ease: "easeInOut" }}
            viewport={{ once: true }}
            className="flex items-center gap-4"
          >
            <Image
              alt="Kozocom Logo"
              src="/images/avatar/avatar.png"
              width={60}
              height={60}
            />
            <h5 className="font-bold text-xl">
              Freelancer | Full-stack Developer
            </h5>
          </motion.div>
          {freelancerTimeLine.map((timeLine) => (
            <motion.div
              initial={{ opacity: 0, y: 100 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: "easeInOut" }}
              viewport={{ once: true }}
              className="relative pl-8 sm:pl-32 py-4 group"
              key={timeLine.title}
            >
              <div className="flex flex-col sm:flex-row items-start mb-1 group-last:before:hidden before:absolute before:left-2 sm:before:left-0 before:h-full before:px-px before:bg-slate-300 sm:before:ml-[6.5rem] before:self-start before:-translate-x-1/2 before:translate-y-3 after:absolute after:left-2 sm:after:left-0 after:w-2 after:h-2 after:bg-indigo-600 after:border-4 after:box-content after:border-slate-50 after:rounded-full sm:after:ml-[6.5rem] after:-translate-x-1/2 after:translate-y-1.5">
                <motion.time
                  initial={{ opacity: 0, y: 100 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.2, ease: "easeInOut" }}
                  viewport={{ once: true }}
                  className="sm:absolute left-0 translate-y-0.5 inline-flex items-center justify-center text-xs font-semibold uppercase w-20 h-6 mb-3 sm:mb-0 text-emerald-600 bg-emerald-100 rounded-full"
                >
                  {timeLine.date}
                </motion.time>
                <motion.div
                  initial={{ opacity: 0, y: 100 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.3, ease: "easeInOut" }}
                  viewport={{ once: true }}
                  className="text-lg font-bold text-slate-900 dark:text-white"
                >
                  {timeLine.title}
                </motion.div>
              </div>
              <motion.div
                initial={{ opacity: 0, y: 100 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.4, ease: "easeInOut" }}
                viewport={{ once: true }}
                className="text-slate-500 text-justify"
              >
                {typeof timeLine.description === "string" ? (
                  <p>{timeLine.description}</p>
                ) : (
                  timeLine.description
                )}
                {timeLine?.listItems && (
                  <ul>
                    {timeLine.listItems.map((item, index) => (
                      <li key={index}>
                        <span className="font-semibold">{item.title}:</span>{" "}
                        {item.content}
                      </li>
                    ))}
                  </ul>
                )}
                <div className="flex gap-2 mt-2 flex-wrap">
                  {timeLine?.techs &&
                    timeLine.techs.map((tech, index) => (
                      <TechBadge key={index} type={tech} />
                    ))}
                </div>
              </motion.div>
            </motion.div>
          ))}

          <div className="relative pl-8 sm:pl-32 py-4 group">
            <div className="flex flex-col sm:flex-row items-start mb-1 group-last:before:hidden before:absolute before:left-2 sm:before:left-0 before:h-full before:px-px before:bg-slate-300 sm:before:ml-[6.5rem] before:self-start before:-translate-x-1/2 before:translate-y-3"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

const DescriptionList: React.FC<DescriptionListProps> = ({ items }) => (
  <ul>
    {items.map((item, index) => (
      <li key={index}>
        <span className="font-semibold">{item.title}:</span> {item.content}
      </li>
    ))}
  </ul>
);

const TechBadgeList: React.FC<TechBadgeListProps> = ({ badges }) => (
  <div className="flex gap-2 mt-2 flex-wrap">
    {badges.map((type, index) => (
      <TechBadge key={index} type={type} />
    ))}
  </div>
);
